<template>
  <div
    :class="[
      'neighborhood-list',
      {
        'is-selected':
          selectedNeighborhoodIds.includes(sibling.id) &&
          !(!neighborhoodLotsCount || neighborhoodLotsCount <= 0),
      },
      { 'coming-soon': isComingSoon },
      { disabled: !neighborhoodLotsCount || neighborhoodLotsCount <= 0 },
    ]"
    @click="selectNeighborhood"
  >
    <div>
      <LazyImage
        class="dsize"
        :src="addScaler(sibling.thumbnail_url)"
        :alt="sibling.neighborhood_name"
        :isNeighborhoodImage="true"
      />
    </div>
    <div class="neighborhood-list-contentwrapper">
      <h4 class="neighborhood-list-communityname">
        {{ sibling.neighborhood_name }}
      </h4>
      <div class="neighborhood-list-desc">
        <p class="neighborhood-list-single" v-if="sibling.home_type">
          {{ sibling.home_type }}
        </p>
        <p class="neighborhood-list-price" v-if="neighborhoodPriceShow">
          Starts From
          <strong>{{ formatPrice(Number(sibling.starting_price)) }}</strong>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRefs } from "vue";
import LazyImage from "./LazyImage.vue";
import {
  NeighboorhoodCoords,
  NeighborhoodPlatMap,
  NeighborhoodSoldDetails,
  Sibling,
} from "../types";
import { ImageWidth, Scalers, ModalType } from "../constant";
import {
  addImageScaler,
  formatPrice as formatPriceUtil,
  getCentrePoint,
  getZoomLevel,
} from "@/core";

const props = defineProps<{
  coordinates?: NeighborhoodPlatMap[];
  sibling: Sibling;
  selectedNeighborhoodIds: string[];
  neighborhoodSoldPercentage: number;
  neighborhoodLotsCount: number;
  soldPercentage: NeighborhoodSoldDetails[];
  isComingSoon: boolean;
}>();
const {
  coordinates,
  sibling,
  selectedNeighborhoodIds,
  neighborhoodSoldPercentage,
  neighborhoodLotsCount,
  soldPercentage,
  isComingSoon,
} = toRefs(props);
const emit = defineEmits<{
  (
    e: "sethoverNeighborZoomLevel",
    payload: {
      zoomLevel: number;
      isDisableHoverNeighbor: boolean;
      centerMap: NeighboorhoodCoords;
    }
  ): void;
  (e: "selectNeighborhood", neighborhoodId: string): void;
  (e: "hideSibling"): void;
  (
    e: "highlightNeighborhood",
    payload: {
      neighborhoodId: string;
      display: string;
    }
  ): void;
}>();

const addScaler = (url: string) =>
  addImageScaler(url, Scalers.TN, ImageWidth.TN);

const formatPrice = (price: number) => formatPriceUtil(price);

const selectNeighborhood = () => {
  if (neighborhoodLotsCount.value > 0) {
    emit("selectNeighborhood", sibling.value.id);
  }
};

const bestZoomClickNeighbor = () => {
  const hoveredNeighborhoodCoorfinates = coordinates.value?.find(
    (el) => el.id === sibling.value.id
  );
  const selectedNeighborhoodCoordinates = coordinates.value?.find(
    (el) => el.id === selectedNeighborhoodIds.value[0]
  );
  if (
    hoveredNeighborhoodCoorfinates &&
    selectedNeighborhoodCoordinates &&
    hoveredNeighborhoodCoorfinates.id !== selectedNeighborhoodCoordinates.id
  ) {
    const coordintes = [
      {
        latitude: hoveredNeighborhoodCoorfinates.latitude,
        longitude: hoveredNeighborhoodCoorfinates.longitude,
      },
      {
        latitude: selectedNeighborhoodCoordinates.latitude,
        longitude: selectedNeighborhoodCoordinates.longitude,
      },
    ];
    const mapSize = document.getElementsByClassName(
      "vue-map-container"
    )[0] as HTMLElement;
    const zoomLevel = getZoomLevel(
      coordintes,
      mapSize.clientWidth,
      mapSize.clientHeight
    );
    const center = getCentrePoint(coordintes);
    emit("sethoverNeighborZoomLevel", {
      zoomLevel,
      isDisableHoverNeighbor: false,
      centerMap: center,
    });
  }
};

const highlightHoveredNeighborhood = () => {
  bestZoomClickNeighbor();
  const overlay = document.getElementById("Overlay-Rectangle");
  if (overlay) overlay.style.display = "flex";

  soldPercentage.value.forEach((neighborhood) => {
    const element = document.getElementById(neighborhood.neighborhoodId);
    if (element) {
      if (neighborhood.neighborhoodId === sibling.value.id) {
        element.style.display = "flex";
        emit("highlightNeighborhood", {
          neighborhoodId: neighborhood.neighborhoodId,
          display: "flex",
        });
      } else {
        element.style.display = "none";
        emit("highlightNeighborhood", {
          neighborhoodId: neighborhood.neighborhoodId,
          display: "none",
        });
      }
    }
  });
};

const deselectNeighborhood = () => {
  const svg = document.getElementById("Overlay-Rectangle");
  if (svg) svg.style.display = "none";

  emit("sethoverNeighborZoomLevel", {
    zoomLevel: 0,
    isDisableHoverNeighbor: true,
    centerMap: { latitude: 0, longitude: 0 },
  });
  emit("hideSibling");
};

const neighborhoodPriceShow = computed(
  () =>
    !!+sibling.value.starting_price &&
    !isComingSoon.value &&
    neighborhoodSoldPercentage.value !== 100
);
</script>

<style lang="scss">
.neighborhood-list {
  --dsize: 45px;
  background: $white;
  border: 2px solid transparent;
  border-radius: 100vmax;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 11px;
  padding: 6px 10px;
  margin-right: 4px;
  color: $gray-600;
  cursor: pointer;
  min-height: 65px;

  .map-card-imgbanner img {
    --mapCardImgHeight: var(--dsize);
    border-radius: inherit;
  }

  &.disabled {
    pointer-events: none;
    cursor: default;
    background-color: #b2b2b4 !important;
  }

  &.is-selected {
    border-color: $lime-green-new;
    background: $beige-700;
  }

  &.coming-soon {
    position: relative;
    padding-bottom: 24px;

    &:after {
      content: "Coming Soon";
      background: $lime-green-new;
      border: 1px solid rgba(241, 231, 209, 0.21);
      position: absolute;
      bottom: -8px;
      width: max-content;
      border-radius: inherit;
      text-align: center;
      color: $white;
      @include font(12px, 600);
      padding: 3px 10px;
      margin-inline: auto;
      left: 0;
      right: 0;
      line-height: 14px;
    }
  }

  &-communityname {
    @include font(12px, 600);
    max-width: 140px;
  }

  &-contentwrapper {
    margin-bottom: auto;
    width: 100%;
  }

  &-single {
    @include font(10px, 600);
  }

  &-price {
    @include font(8px, 400);
    color: $gray-500;
  }

  strong {
    @include font(10px, 700);
    color: $gray-600;
  }

  &-desc {
    width: calc(100% - 40px);

    @include md {
      width: 100%;
    }
  }
}
</style>
